import React, { memo, useCallback, useMemo } from 'react'

import PropTypes from 'prop-types'
import {
  either,
  join,
  pipe,
  prop,
  props as pluckAll,
} from 'ramda'

import { ButtonBase, CardMedia } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { Can } from '~/src/Flags'
import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import { idPropType } from '~/src/Lib/PropTypes'
import { EMPTY_ARRAY, EMPTY_OBJECT, useFilestackURL } from '~/src/Lib/Utils'
import { allowedImageTypes } from '~/src/Lib/Utils/hooks/filestack'
import { urls } from '~/src/Task/bundle'
import { useIAmLead } from '~/src/Task/utils'
import emptyInstruction from '~/src/UI/Assets/EmptyInstruction.svg'
import AttachmentButton from '~/src/UI/Shared/AttachmentButton'
import Button from '~/src/UI/Shared/Button'
import MentionField from '~/src/UI/Shared/DescriptionMentions'
import T from '~/src/UI/Shared/Typography'

const global = getGlobal()
const displayName = 'ViewInstruction'

const logger = createLogger(displayName)

export const styles = theme => ({
  root: {
    paddingTop: theme.spacing(1),
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: 400
  },
  emptyInstructionContent: {
    paddingTop: theme.spacing(1)
  },
  emptyInstructionSvg: {
    display: 'flex',
    justifyContent: 'center'
  },
  emptyInstructionText: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(-2),
    '& p, a': {
      fontSize: '1rem',
    },
    '& a': {
      fontWeight: 'bold'
    }
  },
  instruction: {
    maxWidth: '42rem',
    wordWrap: 'break-word',
    whiteSpace: 'pre-line',
    '& img': {
      height: 75,
      width: 'fit-content',
      objectFit: 'contain',
    },
    '& $image': {
      transition: 'all .3s ease-in-out',
    }
  },
  instructionDescription: {
    fontSize: '1rem',
    color: theme.palette.contrast.strong,
    '& > span': {
      fontSize: '1rem',
      fontWeight: '400 !important'
    }
  },
  attachments: {
    display: 'flex',
    padding: theme.spacing(2, 0),
    gap: theme.spacing(2),
    overflowX: 'auto'
  },
  image: {},
  documentAttachments: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
})

const useStyles = makeStyles(styles)

const EmptyInstruction = ({
  disabled,
  noEmptyState,
  task
}) => {
  const classes = useStyles()
  const iAmLead = useIAmLead(task)

  return (
    noEmptyState ? null : (
      <div className={classes.emptyInstructionContent}>
        <div className={classes.emptyInstructionSvg}>
          <img src={emptyInstruction} style={{ maxWidth: '100%' }} alt="Empty Instruction svg" />
        </div>
        <div className={classes.emptyInstructionText}>
          <T>No Instruction entered.</T>
          <Can any actions={['change_task', iAmLead]}>
            <Button
              aria-controls="add-staff-button"
              aria-label="Add instruction"
              color="primary"
              disabled={disabled}
              variant="text"
              href={`#${urls.getUrl({ id: task.id, action: 'edit' })}`}
            >
              Add Instruction
            </Button>
          </Can>
        </div>
      </div>
    )
  )
}

const ViewInstruction = ({
  dimensions = EMPTY_OBJECT,
  fullscreenHandle,
  noEmptyState = false,
  setSelectedAttachment,
  task
}) => {
  const classes = useStyles()
  const filestackURL = useFilestackURL()
  const { attachment = EMPTY_ARRAY, completedOn, description = '', descriptionMentions = EMPTY_ARRAY, inflight = null } = task
  const { height = EMPTY_OBJECT, width = EMPTY_OBJECT } = dimensions

  const disabled = useMemo(() => Boolean(completedOn || inflight === 'save'), [completedOn, inflight])
  const instructionAvailable = Boolean(description?.length || descriptionMentions?.length || attachment?.length)

  const getFilestackURL = useCallback(filestackFile => filestackURL(
    filestackFile,
    { transforms: 'AUTO_THUMBNAIL', height: height.window, width: width.window },
  ), [filestackURL, height.window, width.window])

  const fileKey = either(
    prop('handle'),
    pipe(pluckAll(['name', 'size', 'type']), join('-'))
  )

  const handleImageClick = useCallback((file, isFile) => {
    if (isFile) {
      global.open(getFilestackURL(file), '_blank', 'noreferrer')
    } else {
      fullscreenHandle.enter()
      setSelectedAttachment(file)
    }
  }, [getFilestackURL, fullscreenHandle, setSelectedAttachment])

  const imgAttachments = useMemo(() => (attachment ?? EMPTY_ARRAY).filter(({ mimetype, type }) => (
    allowedImageTypes.includes(mimetype || type)
  )).map(file => ({
    ...file,
    imageURL: filestackURL(file),
    thumbnailURL: filestackURL(file, ['resize=height:75']),
  })), [attachment, filestackURL])

  const pdfAttachments = useMemo(() => (attachment ?? EMPTY_ARRAY).filter(({ mimetype, type }) => (
    (mimetype || type || '').includes('pdf')
  )).map(file => ({
    ...file,
    extension: file.name.slice(file.name.lastIndexOf('.')),
    nameWithoutExtension: file.name.slice(0, file.name.lastIndexOf('.')),
  })), [attachment])

  return (
    <div className={classes.root}>
      {instructionAvailable ? (
        <div className={classes.instruction}>
          <MentionField
            asText
            asTextProps={{ className: classes.instructionDescription }}
            value={description}
          />

          {imgAttachments.length ? (
            <div className={classes.attachments}>
              {imgAttachments.map(file => (
                <div
                  key={fileKey(file)}
                  className={classes.image}
                >
                  <ButtonBase
                    aria-label={`${file.name} thumbnail`}
                    onClick={() => handleImageClick(file, false)}
                  >
                    <CardMedia
                      alt={file.name}
                      aria-label={`${file.name} image`}
                      component="img"
                      image={file.thumbnailURL}
                    />
                  </ButtonBase>
                </div>
              ))}
            </div>
          ) : null}

          {pdfAttachments.length ? (
            <div className={classes.documentAttachments}>
              {pdfAttachments.map(file => (
                <AttachmentButton
                  key={fileKey(file)}
                  attachment={file}
                  onClick={() => handleImageClick(file, true)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {!instructionAvailable ? (
        <EmptyInstruction
          noEmptyState={noEmptyState}
          disabled={disabled}
          task={task}
        />
      ) : null}
    </div>
  )
}

ViewInstruction.displayName = displayName

ViewInstruction.propTypes = {
  dimensions: PropTypes.shape({
    height: PropTypes.objectOf(PropTypes.number),
    width: PropTypes.objectOf(PropTypes.number),
  }).isRequired,
  fullscreenHandle: PropTypes.shape({
    active: PropTypes.bool,
    exit: PropTypes.func,
    enter: PropTypes.func
  }).isRequired,
  noEmptyState: PropTypes.bool,
  setSelectedAttachment: PropTypes.func.isRequired,
  task: PropTypes.shape({
    attachment: PropTypes.arrayOf(PropTypes.shape({
      filename: PropTypes.string,
      handle: PropTypes.string,
      mimetype: PropTypes.string,
      name: PropTypes.string,
      url: PropTypes.string,
    })),
    completedOn: PropTypes.bool,
    description: PropTypes.string,
    descriptionMentions: PropTypes.arrayOf(idPropType),
    inflight: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])])
  }).isRequired,
}

const Memoized = memo(ViewInstruction)

export default Memoized
